import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import MarcaOSIMTracker          from '../../components/Tracker/MarcaOSIM';
import MarcaEUIPOTracker         from '../../components/Tracker/MarcaEUIPO';
import MarcaWIPOTracker          from '../../components/Tracker/MarcaWIPO';
import MarcaInternationalaTracker from '../../components/Tracker/MarcaInternationala';
import IDepotTracker             from '../../components/Tracker/IDepot';
import DesignMITracker           from '../../components/Tracker/DesignMI';
import BrevetTracker             from '../../components/Tracker/Brevet';
import DosarInstantaTracker      from '../../components/Tracker/DosarInstanta';
import FacturiAsociate from '../../components/Shared/FacturiAsociate';
import { listAllProceduri, updateProcedura } from '../../services/proceduri';
// getProcTitle is still needed here (ProcModal computes it live from
// in-progress unsaved edits, not from the saved API value — see the
// ClientiPage.js getProcWorstSev precedent in ai-agent-implementation-plan.md
// Phase 0, 0.4). Everything else now comes from the API — see the rows mapping below.
import { getProcTitle } from '../../utils/procStatus';
import { FilterClearBtn, FilterCountHint, FilterPill } from '../../components/Shared/FilterPill';
import DateField from '../../components/Shared/DateField';
import { fmtDateRO } from '../../utils/dateFmt';
import './OficiuPage.css';

const TRACKER_MAP = {
  marca_osim:           MarcaOSIMTracker,
  marca_euipo:          MarcaEUIPOTracker,
  marca_wipo:           MarcaWIPOTracker,
  marca_internationala: MarcaInternationalaTracker,
  idepot:               IDepotTracker,
  dmi:                  DesignMITracker,
  brevet:               BrevetTracker,
  dosar_instanta:       DosarInstantaTracker,
};

const TIP_LABEL = {
  marca_osim:           'Marca OSIM',
  marca_euipo:          'Marca EUIPO',
  marca_wipo:           'Marca WIPO',
  marca_internationala: 'Marca Internationala',
  idepot:               'I-Depot',
  dmi:                  'Design / Model Industrial',
  brevet:               'Brevet / Model Utilitate',
  dosar_instanta:       'Dosar Instanta',
  opozitie_osim:        'Opozitie OSIM',
  opozitie_euipo:       'Opozitie EUIPO',
  contestatie_osim:     'Contestatie OSIM',
  cai_atac_euipo:       'Cai Atac EUIPO',
  notificare_aviz:      'Aviz / Notificare',
};

const TIP_COLORS = {
  dosar_instanta:   { bg: '#dbeafe', color: '#1d4ed8' },
  opozitie_osim:    { bg: '#ede9fe', color: '#6d28d9' },
  opozitie_euipo:   { bg: '#e0e7ff', color: '#4338ca' },
  contestatie_osim: { bg: '#fef3c7', color: '#92400e' },
  cai_atac_euipo:   { bg: '#fee2e2', color: '#991b1b' },
  notificare_aviz:  { bg: '#d1fae5', color: '#065f46' },
};

/* ── Toast ── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg) => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

/* ── Proc Modal ── */
function ProcModal({ proc, onClose, onUpdated, showToast }) {
  const [localData, setLocalData] = useState(proc.data || {});
  const [saving, setSaving] = useState(false);
  const Tracker = TRACKER_MAP[proc.tip_proc] || null;
  const title = getProcTitle(proc.tip_proc, localData);
  const tipLabel = TIP_LABEL[proc.tip_proc] || proc.tip_proc;

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateProcedura(proc.id, { data: localData });
      onUpdated({ ...proc, data: localData, ...result.procedura });
      showToast('✓ Procedura salvata');
    } catch {
      showToast('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box op-modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-sticky-header">
          <div className="op-modal-head">
            <div>
              <div className="op-modal-title">{title !== '—' ? title : tipLabel}</div>
              <div className="op-modal-sub">
                <span className="op-modal-client">{proc.client_name}</span>
                <span className="op-modal-sep">·</span>
                <span className="op-modal-tip">{tipLabel}</span>
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-scroll-body">
          {Tracker ? (
            <>
              <Tracker data={localData} onChange={setLocalData} procId={proc.id} clientId={proc.client_id} showToast={showToast} />
              <FacturiAsociate procId={proc.id} clientId={proc.client_id} showToast={showToast} />
              <div className="op-modal-footer">
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Se salveaza...' : (
                    <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Salveaza</>
                  )}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={onClose}>Inchide</button>
              </div>
            </>
          ) : (
            <div className="op-no-tracker">Tracker pentru <strong>{tipLabel}</strong> — in curand.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Sorting ── */
const _roColl = new Intl.Collator('ro', { sensitivity: 'base', numeric: true });
const _SEV_RANK = { overdue: 0, act: 1, watch: 2, info: 3, neutral: 4, done: 5 };
const _cmpData = (dir) => (a, b) => {
  const x = a._dataDepunere || '', y = b._dataDepunere || '';
  if (!x && !y) return 0;
  if (!x) return 1;             // rows without a date always go last
  if (!y) return -1;
  return dir === 'asc' ? x.localeCompare(y) : y.localeCompare(x);
};
const _dataDesc = _cmpData('desc');
const SORTERS = {
  data_desc: _dataDesc,
  data_asc:  _cmpData('asc'),
  marca:     (a, b) => _roColl.compare(a._title || '', b._title || '')       || _dataDesc(a, b),
  status:    (a, b) => (_SEV_RANK[a._status.sev] ?? 9) - (_SEV_RANK[b._status.sev] ?? 9) || _dataDesc(a, b),
  client:    (a, b) => _roColl.compare(a.client_name || '', b.client_name || '') || _dataDesc(a, b),
};

/* ── Status badge ── */
function StatusBadge({ status }) {
  return (
    <span className={`op-status op-status-${status.sev}`}>
      {status.label}
    </span>
  );
}

/* ── OficiuPage ── */
export default function OficiuPage({ title, tips, icon }) {
  const { viewMode } = useAuth();
  const [proceduri, setProceduri] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const { toasts, show: showToast } = useToast();
  const location = useLocation();
  const showTipCol = tips.length > 1;

  // Filters
  const [fSearch, setFSearch]     = useState('');
  const [fTip, setFTip]           = useState('');
  const [fStatus, setFStatus]     = useState('');
  const [fClient, setFClient]     = useState('');
  const [fDataDe, setFDataDe]     = useState('');
  const [fDataPana, setFDataPana] = useState('');
  const [sortBy, setSortBy]       = useState('data_desc');

  useEffect(() => {
    setLoading(true);
    listAllProceduri(tips)
      .then(d => setProceduri(d.proceduri || []))
      .catch(() => showToast('Eroare la incarcarea procedurilor'))
      .finally(() => setLoading(false));
  }, [tips.join(','), viewMode]); // eslint-disable-line

  useEffect(() => {
    // location.state covers in-app navigation; the query param covers
    // external links (e.g. the daily report email).
    const id = location.state?.openProcId || new URLSearchParams(location.search).get('openProcId');
    if (!id || proceduri.length === 0) return;
    setSelectedId(Number(id));
    window.history.replaceState({}, '', location.pathname);
  }, [proceduri, location.state?.openProcId, location.search]);

  const handleUpdated = (updated) => {
    setProceduri(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
  };

  const selectedProc = proceduri.find(p => p.id === selectedId);

  // Derive display rows — status/title/nr/data_depunere now come precomputed
  // from the API (main.rules.procedura_status, ported 1:1 from procStatus.js
  // and parity-verified against real data — see ai-agent-implementation-plan.md
  // Phase 0). No local computation here anymore.
  const rows = proceduri.map(p => ({
    ...p,
    _title:        p.title,
    _nr:           p.nr,
    _dataDepunere: p.data_depunere,
    _status:       p.status,
    _tipLabel:     TIP_LABEL[p.tip_proc] || p.tip_proc,
  }));

  // Unique filter options
  const statusOptions = [...new Set(rows.map(r => r._status.label))].sort();
  const clientOptions = [...new Set(rows.map(r => r.client_name))].sort();

  const filtered = rows.filter(r => {
    if (fSearch) {
      const q = fSearch.toLowerCase();
      const matchTitle = (r._title || '').toLowerCase().includes(q);
      const matchNr    = (r._nr   || '').toLowerCase().includes(q);
      if (!matchTitle && !matchNr) return false;
    }
    if (fTip    && r.tip_proc           !== fTip)    return false;
    if (fStatus && r._status.label      !== fStatus) return false;
    if (fClient && r.client_name        !== fClient) return false;
    if (fDataDe   && r._dataDepunere && r._dataDepunere < fDataDe)   return false;
    if (fDataPana && r._dataDepunere && r._dataDepunere > fDataPana) return false;
    return true;
  });

  const sorted = [...filtered].sort(SORTERS[sortBy] || SORTERS.data_desc);

  return (
    <main className="cp-main">
      <div className="topbar">
        <div className="topbar-left">
          {icon && <span className="op-page-icon">{icon}</span>}
          <h1 className="page-title">{title}</h1>
          <span className="count-badge">{proceduri.length}</span>
        </div>
      </div>

      <div className="content">
        {/* Filter row */}
        <div className="filter-bar">
          <input
            className="search-input"
            style={{ minWidth: 220 }}
            placeholder="Cauta denumire sau nr. dosar…"
            value={fSearch}
            onChange={e => setFSearch(e.target.value)}
          />
          {showTipCol && (
            <FilterPill value={fTip} onChange={e => setFTip(e.target.value)}>
              <option value="">Tip</option>
              {tips.map(t => <option key={t} value={t}>{TIP_LABEL[t] || t}</option>)}
            </FilterPill>
          )}
          <FilterPill value={fClient} onChange={e => setFClient(e.target.value)} style={{ maxWidth: 220 }}>
            <option value="">Client</option>
            {clientOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </FilterPill>
          <FilterPill value={fStatus} onChange={e => setFStatus(e.target.value)}>
            <option value="">Status</option>
            {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </FilterPill>
          <div style={{ width: 140 }} title="Data deschidere — de la">
            <DateField placeholder="Data — de la" value={fDataDe} onChange={setFDataDe} />
          </div>
          <div style={{ width: 140 }} title="Data deschidere — pana la">
            <DateField placeholder="Data — pana la" value={fDataPana} onChange={setFDataPana} />
          </div>
          <FilterPill value={sortBy} onChange={e => setSortBy(e.target.value || 'data_desc')}>
            <option value="">Sorteaza</option>
            <option value="data_desc">Data depunere — descendent</option>
            <option value="data_asc">Data depunere — ascendent</option>
            <option value="marca">Alfabetic — marca</option>
            <option value="status">Status</option>
            <option value="client">Alfabetic — client</option>
          </FilterPill>
          {(fSearch || fTip || fStatus || fClient || fDataDe || fDataPana || sortBy !== 'data_desc') && (
            <FilterClearBtn onClick={() => { setFSearch(''); setFTip(''); setFStatus(''); setFClient(''); setFDataDe(''); setFDataPana(''); setSortBy('data_desc'); }} />
          )}
          <FilterCountHint count={filtered.length} singular="procedura" plural="proceduri" />
        </div>

        <div className="clients-card">
          <table className="clients-table op-table">
            <thead>
              <tr>
                <th>Denumire</th>
                {showTipCol && <th>Tip</th>}
                <th>Status</th>
                <th>Nr. dosar</th>
                <th>Data depunere</th>
                <th>Client</th>
                <th>Adaugat</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={showTipCol ? 7 : 6} className="op-center-cell">Se incarca...</td>
                </tr>
              )}
              {!loading && sorted.length === 0 && (
                <tr>
                  <td colSpan={showTipCol ? 7 : 6} className="op-center-cell">Nicio procedura gasita.</td>
                </tr>
              )}
              {sorted.map(row => {
                const tipColor = TIP_COLORS[row.tip_proc] || { bg: '#f3f4f6', color: '#6b7280' };
                return (
                <tr key={row.id} onClick={() => setSelectedId(row.id)}
                  className={selectedId === row.id ? 'row-selected' : ''}>
                  <td className="op-title-cell">
                    <div className="op-title">{row._title}</div>
                  </td>
                  {showTipCol && (
                    <td>
                      <span style={{ ...tipColor, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, whiteSpace: 'nowrap' }}>
                        {row._tipLabel}
                      </span>
                    </td>
                  )}
                  <td><StatusBadge status={row._status} /></td>
                  <td>{row._nr || '—'}</td>
                  <td>{fmtDateRO(row._dataDepunere) || '—'}</td>
                  <td className="op-client-cell">{row.client_name}</td>
                  <td>
                    {new Date(row.created_at).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedProc && (
        <ProcModal
          key={selectedProc.id}
          proc={selectedProc}
          onClose={() => setSelectedId(null)}
          onUpdated={handleUpdated}
          showToast={showToast}
        />
      )}

      <div className="toast-wrap">
        {toasts.map(t => <div key={t.id} className="toast">{t.msg}</div>)}
      </div>
    </main>
  );
}
